/**
 * Public landing page (root `/`). Split-screen layout mirroring /login:
 *   LEFT (light)  — logo, welcome heading, CTAs, tiny plan row
 *   RIGHT (green) — hero copy identical to login for brand consistency
 * Authenticated users are redirected to `/dashboard` by `PublicOnly`.
 */
import { Link, Navigate } from "react-router-dom";
import { useState } from "react";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { useSEO } from "@/hooks/useSEO";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { ArrowRight, Play, X, ChevronLeft, ChevronRight } from "lucide-react";

// Screenshot gallery — capturas reales tomadas de la app en preview. Los
// archivos viven en `/public/screenshots/` así que se sirven estáticamente
// sin pasar por la API. Sustitúyelos por versiones con datos reales cuando
// tengas un tenant demo poblado.
const GALLERY = [
  { src: "/screenshots/01-dashboard.jpg",   es: "Panel — KPIs, IVA, resumen trimestral",              en: "Dashboard — KPIs, VAT, quarterly summary" },
  { src: "/screenshots/02-gastos.jpg",      es: "Gastos — OCR con IA + filtros avanzados",            en: "Expenses — AI OCR + advanced filters" },
  { src: "/screenshots/03-ingresos.jpg",    es: "Ingresos — ventas y facturación por cliente",        en: "Income — sales and per-customer invoicing" },
  { src: "/screenshots/04-salarios.jpg",    es: "Salarios — nóminas del equipo mes a mes",            en: "Payroll — team salaries month by month" },
  { src: "/screenshots/05-productos.jpg",   es: "Productos — precios, variación y alertas de subidas", en: "Products — prices, variation and price-hike alerts" },
  { src: "/screenshots/06-proveedores.jpg", es: "Proveedores — desglose mensual expandible por CIF",  en: "Vendors — expandable monthly breakdown per CIF" },
  { src: "/screenshots/07-carta.jpg",       es: "Carta y Escandallos — food cost real por plato",     en: "Menu — real food cost per dish" },
  { src: "/screenshots/08-inventario.jpg",  es: "Inventario — recuentos valorados por foto",          en: "Inventory — photo-based valued counts" },
  { src: "/screenshots/09-albaranes.jpg",   es: "Albaranes — notas de entrega y alertas de precio",   en: "Delivery notes — with price alerts" },
  { src: "/screenshots/10-tesoreria.jpg",   es: "Tesorería — evolución mensual de saldos bancarios",  en: "Treasury — monthly bank balance evolution" },
];

export default function Landing() {
  const { lang, setLang } = useI18n();
  const { user, loading } = useAuth();
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [slide, setSlide] = useState(0);
  useSEO({
    title: lang === "es"
      ? "ChefSync P&L — Control financiero para restaurantes con IA"
      : "ChefSync P&L — AI-powered financial control for restaurants",
    description: lang === "es"
      ? "ChefSync P&L combina OCR con IA, contabilidad de proveedores y análisis de márgenes por plato. Sube tus gastos e ingresos y todo se calcula automáticamente."
      : "ChefSync P&L combines AI-powered OCR, vendor accounting and per-dish margin analytics. Upload your expenses and income and everything is computed automatically.",
    path: "/",
  });

  if (!loading && user) return <Navigate to="/dashboard" replace />;

  const plans = [
    { name: "Free",     price: "0€",   limit: lang === "es" ? "200 docs · 1 mes prueba · acceso total" : "200 docs · 1-month trial · full access" },
    { name: "Basic",    price: "49€",  limit: lang === "es" ? "100 facturas/mes · acceso limitado" : "100 invoices/mo · limited access" },
    { name: "Pro",      price: "79€",  limit: lang === "es" ? "200 facturas/mes · acceso total" : "200 invoices/mo · full access", highlight: true },
    { name: "Business", price: "149€", limit: lang === "es" ? "400 facturas/mes · acceso total" : "400 invoices/mo · full access" },
  ];

  return (
    <div className="min-h-screen flex">
      {/* LEFT: white column with CTAs */}
      <div className="w-full lg:w-[520px] flex flex-col p-8 lg:p-12 bg-[var(--surface)]">
        <div className="flex items-center justify-between mb-16">
          <Link to="/" className="flex items-center gap-2" data-testid="landing-logo-home">
            <Logo size={32} />
            <span className="font-display font-bold text-base">
              ChefSync <span className="text-[var(--text-muted)] font-normal">P&amp;L</span>
            </span>
          </Link>
          <button
            onClick={() => setLang(lang === "es" ? "en" : "es")}
            data-testid="landing-lang-toggle"
            className="text-xs uppercase tracking-wider text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
          >
            {lang === "es" ? "EN" : "ES"}
          </button>
        </div>

        <div className="flex-1 flex flex-col justify-center max-w-sm">
          <h1 className="font-display text-4xl font-bold tracking-tight leading-none mb-2">
            {lang === "es" ? "Bienvenido" : "Welcome"}
          </h1>
          <p className="text-sm text-[var(--text-secondary)] mb-10">
            {lang === "es" ? "Control financiero, sin esfuerzo." : "Financial control, effortless."}
          </p>

          <div className="flex flex-col gap-2.5 mb-4">
            <Link to="/register" data-testid="landing-cta-primary">
              <Button className="w-full h-11 bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white text-sm">
                {lang === "es" ? "Regístrate gratis" : "Sign up free"}
                <ArrowRight size={14} className="ml-2" />
              </Button>
            </Link>
            <Link to="/login" data-testid="landing-cta-secondary">
              <Button variant="outline" className="w-full h-11 text-sm">
                {lang === "es" ? "Iniciar sesión" : "Log in"}
              </Button>
            </Link>
          </div>
          <p
            className="text-xs text-[var(--text-secondary)] mb-8 leading-relaxed"
            data-testid="landing-trial-note"
          >
            {lang === "es"
              ? "1 mes de prueba en el plan Free con acceso total a todos los módulos — sin tarjeta."
              : "1-month Free-tier trial with full access to every module — no card required."}
          </p>

          <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[var(--text-muted)] mb-3">
            {lang === "es" ? "Planes desde" : "Plans from"}
          </p>
          <div className="grid grid-cols-2 gap-2">
            {plans.map((p) => (
              <div
                key={p.name}
                className={`rounded-lg border px-3 py-2.5 ${p.highlight ? "border-[var(--primary)] bg-[var(--primary)]/5" : "border-[var(--border-color)]"}`}
                data-testid={`landing-plan-${p.name.toLowerCase()}`}
              >
                <div className="flex items-baseline justify-between mb-0.5">
                  <p className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] font-semibold">
                    {p.name}
                  </p>
                  {p.highlight && (
                    <span className="text-[8px] uppercase tracking-wider text-[var(--primary)] font-bold">
                      {lang === "es" ? "Popular" : "Popular"}
                    </span>
                  )}
                </div>
                <p className="font-display text-lg font-bold leading-none">
                  {p.price}
                  {p.name !== "Free" && (
                    <span className="text-[10px] text-[var(--text-muted)] font-normal ml-0.5">
                      {lang === "es" ? "/mes" : "/mo"}
                    </span>
                  )}
                </p>
                <p className="text-[10px] text-[var(--text-secondary)] mt-0.5">{p.limit}</p>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-[var(--text-muted)] mt-2">
            {lang === "es" ? "Precios + IVA · Sin tarjeta para empezar." : "Prices + VAT · No card to start."}
          </p>
        </div>
      </div>

      {/* RIGHT: green hero — identical to /login for brand consistency */}
      <div
        className="hidden lg:flex flex-1 relative items-center p-16"
        style={{ background: "linear-gradient(135deg, #2C4C3B 0%, #1a3025 100%)" }}
        data-testid="landing-hero-panel"
      >
        <div className="text-white max-w-lg">
          <p className="text-xs uppercase tracking-[0.2em] opacity-70 mb-6">
            {lang === "es" ? "Software financiero para hostelería" : "Financial software for restaurants"}
          </p>
          <h2 className="font-display text-4xl font-bold leading-tight mb-6">
            {lang === "es"
              ? "Facturas por OCR, gastos, ingresos, escandallos, inventario, tesorería y alertas — todo tu restaurante en una app."
              : "OCR invoices, expenses, income, dish costing, inventory, treasury and alerts — your whole restaurant in one app."}
          </h2>
          <p className="text-base opacity-90 leading-relaxed">
            {lang === "es"
              ? "ChefSync P&L combina OCR con IA, contabilidad de proveedores y análisis de márgenes por plato. Sube tus gastos e ingresos y todo se calcula automáticamente."
              : "ChefSync P&L combines AI-powered OCR, vendor accounting and per-dish margin analytics. Upload your expenses and income and everything is computed automatically."}
          </p>
          <button
            type="button"
            onClick={() => { setSlide(0); setGalleryOpen(true); }}
            data-testid="landing-open-gallery"
            className="mt-6 inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-white/10 hover:bg-white/20 border border-white/20 text-sm font-medium transition-colors backdrop-blur-sm"
          >
            <Play size={14} className="fill-white" />
            {lang === "es" ? "Ver capturas de la app" : "See app screenshots"}
          </button>
        </div>
      </div>

      {galleryOpen && (
        <GalleryModal
          slide={slide}
          setSlide={setSlide}
          onClose={() => setGalleryOpen(false)}
          lang={lang}
        />
      )}
    </div>
  );
}


function GalleryModal({ slide, setSlide, onClose, lang }) {
  const current = GALLERY[slide];
  const prev = () => setSlide((slide - 1 + GALLERY.length) % GALLERY.length);
  const next = () => setSlide((slide + 1) % GALLERY.length);
  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="landing-gallery-modal"
      className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <button
        type="button"
        onClick={onClose}
        data-testid="landing-gallery-close"
        className="absolute top-4 right-4 text-white/70 hover:text-white p-2 rounded-full hover:bg-white/10 transition"
        aria-label={lang === "es" ? "Cerrar" : "Close"}
      >
        <X size={22} />
      </button>

      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); prev(); }}
        data-testid="landing-gallery-prev"
        className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-2 rounded-full hover:bg-white/10 transition"
        aria-label={lang === "es" ? "Anterior" : "Previous"}
      >
        <ChevronLeft size={28} />
      </button>

      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); next(); }}
        data-testid="landing-gallery-next"
        className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-2 rounded-full hover:bg-white/10 transition"
        aria-label={lang === "es" ? "Siguiente" : "Next"}
      >
        <ChevronRight size={28} />
      </button>

      <div
        className="max-w-5xl w-full flex flex-col items-center gap-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="w-full rounded-lg overflow-hidden shadow-2xl bg-white">
          <img
            key={current.src}
            src={current.src}
            alt={lang === "es" ? current.es : current.en}
            className="w-full h-auto max-h-[70vh] object-contain bg-white"
            data-testid={`landing-gallery-img-${slide}`}
          />
        </div>
        <p className="text-white/90 text-sm text-center max-w-2xl" data-testid="landing-gallery-caption">
          {lang === "es" ? current.es : current.en}
        </p>
        <div className="flex items-center gap-1.5">
          {GALLERY.map((g, i) => (
            <button
              type="button"
              key={g.src}
              onClick={() => setSlide(i)}
              data-testid={`landing-gallery-dot-${i}`}
              aria-label={`${i + 1} / ${GALLERY.length}`}
              className={`h-1.5 rounded-full transition-all ${
                i === slide ? "w-6 bg-white" : "w-1.5 bg-white/40 hover:bg-white/60"
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
